// Copyright (c) Docugami, Inc. All rights reserved.

import { MutableRefObject, useEffect, useRef, useState } from "react";
import { ExternalRenderComponent } from "sdv/components/ExternalRenderComponent";
import { ComponentId } from "sdv/engine/Component";
import { Engine } from "sdv/engine/Engine";
import { Entity } from "sdv/engine/Entity";

interface ISdvRenderable {
    ref: MutableRefObject<HTMLDivElement | null>;
    entity: Entity | null;
}

export const useSdvRenderable = (components: ComponentId[], engine: Engine | null): ISdvRenderable => {
    const [entity, setEntity] = useState<Entity | null>(null);
    const elementRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!engine) {
            return;
        }

        const onAdded = (newEntity: Entity) => {
            const renderComp = newEntity.getComponent<ExternalRenderComponent>(ComponentId.ExternalRender)!;
            renderComp.element = elementRef;

            setEntity(newEntity);
        }

        const onRemoved = () => {
            setEntity(null);
        }

        const query = engine.sdvInterface.entities.query(components);
        const currentEntity = query.single();
        if (!!currentEntity) {
            onAdded(currentEntity);
        }

        const unregisterAdded = query.entityAdded.addListener(onAdded);
        const unregisterRemoved = query.entityRemoved.addListener(onRemoved);

        return () => {
            unregisterAdded();
            unregisterRemoved();
            setEntity(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [engine]);

    return {
        ref: elementRef,
        entity: entity
    }
}
